import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { FormikErrors } from 'formik';
import { PasswordResetForm } from 'models';

import PageLayout from 'layouts/PageLayout';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Input, ValidationWarning } from 'components';

import {
  CheckIconWrapper,
  Container,
  Description,
  Label,
  StyledCheckIcon,
  StyledCloseIcon,
  StyledIconButton,
  StyledLoader,
  SuccessLabel,
  SuccessTitle,
  Title,
} from './style';

interface Props {
  description: string;
  values: PasswordResetForm;
  errors: FormikErrors<PasswordResetForm>;
  loading: boolean;
  error: boolean;
  isSuccess: boolean;
  errorMessage: string | null;
  handleChangeInput: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInitErrors: () => void;
  handleSubmit: () => void;
}

function boldenEmail(description: string) {
  return description.replace(
    /[a-z0-9._%+-]+\@([a-z0-9.-])+\.([a-z]{2,})/g,
    '<b>$&</b>'
  );
}

const PasswordResetView: React.FC<Props> = ({
  description,
  values,
  errors,
  error,
  loading,
  errorMessage,
  isSuccess,
  handleSubmit,
  handleChangeInput,
  handleInitErrors,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const forwardToLoginPage = () => {
    history.push('/');
  };

  const renderDefaultView = () => {
    return (
      <>
        {loading && <StyledLoader color="secondary" />}
        <StyledIconButton onClick={loading ? undefined : forwardToLoginPage}>
          <StyledCloseIcon />
        </StyledIconButton>
        <Description error={!!errorMessage}>
          {errorMessage || t('ResetPasswordText')}
        </Description>
        <Stack
          component="form"
          spacing={3}
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <Stack spacing={2}>
            <Label>{t('#portal.usersettings.label.email')}</Label>
            <ValidationWarning
              text={errors.email}
              handleInitErrors={handleInitErrors}
              Component={
                <Input
                  name="email"
                  value={values.email}
                  onChange={handleChangeInput}
                />
              }
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Button
              color="secondary"
              disabled={loading}
              type="button"
              onClick={forwardToLoginPage}
            >
              {t('ToLoginPage')}
            </Button>
            <Button color="secondary" disabled={loading} type="submit">
              {t('Send')}
            </Button>
          </Stack>
        </Stack>
      </>
    );
  };

  const renderSuccessView = () => {
    return (
      <Stack alignItems="center" justifyContent="center">
        <CheckIconWrapper>
          <StyledCheckIcon />
        </CheckIconWrapper>
        <SuccessTitle>{t('Step2Header')}</SuccessTitle>
        <SuccessLabel
          dangerouslySetInnerHTML={{ __html: boldenEmail(description) }}
        />
        <Button color="secondary" onClick={forwardToLoginPage}>
          {t('SignOut')}
        </Button>
      </Stack>
    );
  };

  return (
    <PageLayout isError={error}>
      <Stack alignItems="center">
        <Title>{t('ResetPasswordHeader')}</Title>
        <Container>
          {isSuccess ? renderSuccessView() : renderDefaultView()}
        </Container>
      </Stack>
    </PageLayout>
  );
};

export default PasswordResetView;
